import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AvailabilityRuleListItem } from '@app/vinter/models/availability-rule/availability-rule-list-item';
import { SchedulingEventForm } from '@app/vinter/models/scheduling-event/scheduling-event-form-item';
import { SchedulingEventListItem } from '@app/vinter/models/scheduling-event/scheduling-event-list-item';
import { UserIntegrationForm } from '@app/vinter/models/user-integration/user-integration-form-item';
import { AvailabilityRuleService } from '@app/vinter/services/availability-rule.service';
import { SchedulingEventService } from '@app/vinter/services/scheduling-event.service';
import { UserIntegrationService } from '@app/vinter/services/user-integration.service';
import { CredentialsService } from '@app/core';
import { EntityStateEnum } from '@app/shared/common/enums';
import { TranslateService } from '@ngx-translate/core';

const INTEGRATION_ICON_MAP: Record<number, string> = {
  1: '/assets/vinter/icons/microsoft-tems-icon.svg',
  2: '/assets/vinter/icons/google-meet-icon.svg',
  3: '/assets/vinter/icons/zoom-meeting-icon.svg',
};

const INTEGRATION_LABEL_MAP: Record<number, string> = {
  1: 'Microsoft Teams',
  2: 'Google Meet',
  3: 'Zoom',
};

export interface DurationOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-new-event-sidebar',
  templateUrl: './new-event-sidebar.component.html',
  styleUrls: ['./new-event-sidebar.component.scss']
})
export class NewEventSidebarComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() editEvent: SchedulingEventListItem | null = null;
  @Input() readonly: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  form: SchedulingEventForm = this.emptyForm();

  availabilityRules: AvailabilityRuleListItem[] = [];
  selectedRule: AvailabilityRuleListItem | null = null;
  ruleMenuOpen: boolean = false;

  userIntegrations: UserIntegrationForm[] = [];
  selectedIntegrationId: string | null = null;

  get displayedIntegrations(): UserIntegrationForm[] {
    if (this.readonly && this.editEvent?.userIntegrationID) {
      return this.userIntegrations.filter(i => i.id === this.editEvent!.userIntegrationID);
    }
    return this.userIntegrations;
  }

  durationOptions: DurationOption[] = [];

  constructor(
    private availabilityRuleService: AvailabilityRuleService,
    private userIntegrationService: UserIntegrationService,
    private schedulingEventService: SchedulingEventService,
    private credentialService: CredentialsService,
    private translate: TranslateService,
  ) {
    this.durationOptions = [
      { label: this.translate.instant('Key.InterviewDuration30'), value: 30 },
      { label: this.translate.instant('Key.InterviewDuration60'), value: 60 },
    ];
  }

  ngOnInit(): void {
    this.loadDependencies();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.loadDependencies();
    }
  }

  private loadDependencies(): void {
    const userId = this.credentialService.getNameIdentifier();
    this.availabilityRuleService.getListByFilter({ userID: userId }).subscribe(data => {
      this.availabilityRules = data ?? [];
      if (this.visible) {
        this.initForm();
      }
    });
    this.userIntegrationService.getListByFilter({ userID: userId }).subscribe(data => {
      this.userIntegrations = data ?? [];
      if (this.visible && this.editEvent) {
        this.selectIntegrationById(this.editEvent.userIntegrationID);
      }
    });
  }

  private initForm(): void {
    if (this.editEvent) {
      this.form = {
        id: this.editEvent.id,
        name: this.editEvent.name,
        isActive: this.editEvent.isActive,
        durationMinutes: this.editEvent.durationMinutes,
        startStepMinutes: this.editEvent.startStepMinutes,
        userIntegrationID: this.editEvent.userIntegrationID,
        availabilityRuleID: this.editEvent.availabilityRuleID,
        entityState: EntityStateEnum.Modified,
        xmin: this.editEvent.xmin,
      };
      this.selectRuleById(this.editEvent.availabilityRuleID);
      this.selectIntegrationById(this.editEvent.userIntegrationID);
    } else {
      this.form = this.emptyForm();
      this.selectedIntegrationId = null;
      if (this.availabilityRules.length > 0) {
        this.onRuleSelect(this.availabilityRules[0]);
      } else {
        this.selectedRule = null;
      }
    }
  }

  private selectRuleById(id: string): void {
    const rule = this.availabilityRules.find(r => r.id === id) ?? null;
    if (rule) {
      this.selectedRule = rule;
    }
  }

  private selectIntegrationById(id: string): void {
    const integration = this.userIntegrations.find(i => i.id === id) ?? null;
    if (integration) {
      this.selectedIntegrationId = integration.id;
    }
  }

  getIconPath(type: number): string {
    return INTEGRATION_ICON_MAP[type] ?? INTEGRATION_ICON_MAP[3];
  }

  getIntegrationLabel(type: number): string {
    return INTEGRATION_LABEL_MAP[type] ?? '';
  }

  toggleRuleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.ruleMenuOpen = !this.ruleMenuOpen;
  }

  onRuleSelect(rule: AvailabilityRuleListItem): void {
    this.selectedRule = rule;
    this.form.availabilityRuleID = rule.id;
    this.ruleMenuOpen = false;
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.ruleMenuOpen = false;
  }

  onIntegrationSelect(integration: UserIntegrationForm): void {
    this.selectedIntegrationId = integration.id;
    this.form.userIntegrationID = integration.id;
  }

  save(): void {
    if (!this.form.name?.trim() || !this.form.durationMinutes || !this.form.availabilityRuleID || !this.form.userIntegrationID) {
      return;
    }
    this.schedulingEventService.save(this.form).subscribe(() => {
      this.saved.emit();
      this.close();
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private emptyForm(): SchedulingEventForm {
    return {
      id: crypto.randomUUID(),
      name: '',
      isActive: true,
      durationMinutes: null,
      startStepMinutes: 0,
      userIntegrationID: null,
      availabilityRuleID: null,
      entityState: EntityStateEnum.Added,
      xmin: '0',
    };
  }
}
